// Zwift Ride / Play / Click controllers (reverse-engineered protocol, experimental).
import { t } from '../i18n/index.js';
import { ZW_SVC, ZW_SVC16 } from './discovery.js';
import { valueOf, type BtDevice, type GattService, type StatusText } from './ble.js';
const ZW_ASYNC = '00000002-19ca-4651-86e5-fa29dcdd09d1';
const ZW_SYNC_RX = '00000003-19ca-4651-86e5-fa29dcdd09d1';
const ZW_SYNC_TX = '00000004-19ca-4651-86e5-fa29dcdd09d1';
const RIDE_ON = [0x52, 0x69, 0x64, 0x65, 0x4f, 0x6e];

// Zwift Ride button mask (bit at 0 = pressed), based on open-source projects that decoded the protocol.
// Labels of known bits: t.devices.buttons.
export const DEFAULT_RIDE_MAP = {
  bit0: 'steerLeft', bit2: 'steerRight', bit1: 'fire', bit4: 'fire', bit11: 'fire', bit16: 'fire',
  bit9: 'gearDown', bit10: 'gearDown', bit13: 'gearUp', bit14: 'gearUp', bit3: 'look',
};

function readVarint(b: Uint8Array, o: number): [number, number] | null {
  let x = 0, sh = 0, c: number;
  do { if (o >= b.length) return null; c = b[o++]; x += (c & 0x7f) * 2 ** sh; sh += 7; } while (c & 0x80 && sh < 56);
  return [x, o];
}
// Minimal protobuf decoding: returns {field number: varint value}
function protoVarints(b: Uint8Array, o: number): Record<number, number> {
  const out: Record<number, number> = {};
  while (o < b.length) {
    const k = readVarint(b, o); if (!k) break; o = k[1];
    const f = Math.floor(k[0] / 8), wt = k[0] & 7;
    if (wt === 0) { const v = readVarint(b, o); if (!v) break; o = v[1]; if (!(f in out)) out[f] = v[0]; }
    else if (wt === 2) { const n = readVarint(b, o); if (!n) break; o = n[1] + n[0]; }
    else if (wt === 5) o += 4; else if (wt === 1) o += 8; else break;
  }
  return out;
}

/** What the player reads for a controller: its name, or the brand when it advertises none. */
const nameOf = (d: BtDevice) => d.name || 'Zwift';

export class ZwiftController {
  devs: BtDevice[];
  onButton: (key: string, down: boolean) => void;
  onRaw?: (tag: string, bytes: Uint8Array) => void;
  onStatus: (text: StatusText, cls?: 'ok' | 'bad') => void;
  /** Buttons held, per controller: left and right add up, and one controller's packet says nothing of the other's. */
  held = new Map<string, Set<string>>();
  /** Ride bits seen released at least once, per controller. */
  seen = new Map<string, Set<number>>();
  /** The disconnect listener of each controller, so adopting it again does not stack another. */
  private onDrop = new Map<string, () => void>();
  constructor(onButton: (key: string, down: boolean) => void, onStatus: (text: StatusText, cls?: 'ok' | 'bad') => void, onRaw?: (tag: string, bytes: Uint8Array) => void) {
    this.onButton = onButton; this.onStatus = onStatus; this.onRaw = onRaw;
    this.devs = [];
  }
  /** Whether any controller holds `k`. */
  private isHeld(k: string) { for (const s of this.held.values()) if (s.has(k)) return true; return false; }
  /** Replaces what controller `src` holds, and reports only the buttons whose combined state changed. */
  private setHeld(src: string, now: Set<string>) {
    const before = this.held.get(src) ?? new Set<string>();
    const wasHeld = new Set([...before, ...now].filter(k => this.isHeld(k)));
    this.held.set(src, now);
    for (const k of now) if (!wasHeld.has(k)) this.onButton(k, true);
    for (const k of before) if (!now.has(k) && !this.isHeld(k)) this.onButton(k, false);
  }
  get connected() { return this.devs.some(d => !!d.gatt?.connected); }

  async connect() {
    if (!navigator.bluetooth) throw new Error(t.devices.noBluetoothShort);
    const dev = await navigator.bluetooth.requestDevice({
      filters: [{ namePrefix: 'Zwift' }, { services: [ZW_SVC] }, { services: [ZW_SVC16] }],
      optionalServices: [ZW_SVC, ZW_SVC16, 'battery_service', 'device_information'],
    });
    await this.adopt(dev);
    return dev;
  }

  /** Connects a device already authorised by the browser. Left and right controllers add up; the same one replaces itself. */
  async adopt(dev: BtDevice) {
    const gatt = dev.gatt;
    if (!gatt) throw new Error(t.devices.disconnected); // not a Bluetooth device we can talk to
    this.devs = this.devs.filter(d => d.id !== dev.id);
    const old = this.onDrop.get(dev.id);
    if (old) dev.removeEventListener('gattserverdisconnected', old);
    const dropped = () => {
      this.setHeld(dev.id, new Set()); // a controller that drops lets go of everything it held
      this.onStatus(() => t.devices.nameDisconnected(nameOf(dev)), this.connected ? 'ok' : 'bad');
    };
    this.onDrop.set(dev.id, dropped);
    dev.addEventListener('gattserverdisconnected', dropped);
    this.onStatus(() => t.devices.connecting);
    const srv = await gatt.connect();
    let svc: GattService;
    try { svc = await srv.getPrimaryService(ZW_SVC16); } catch (e) { svc = await srv.getPrimaryService(ZW_SVC); }
    const asyncC = await svc.getCharacteristic(ZW_ASYNC);
    const rx = await svc.getCharacteristic(ZW_SYNC_RX);
    let tx = null; try { tx = await svc.getCharacteristic(ZW_SYNC_TX); } catch (e) {}
    const tag = nameOf(dev);
    asyncC.addEventListener('characteristicvaluechanged', e => this.parse(new Uint8Array(valueOf(e).buffer), tag, dev.id));
    await asyncC.startNotifications();
    if (tx) { tx.addEventListener('characteristicvaluechanged', e => this.onRaw?.(tag + ' sync', new Uint8Array(valueOf(e).buffer))); await tx.startNotifications(); }
    await rx.writeValueWithResponse(new Uint8Array(RIDE_ON));
    this.devs.push(dev);
    const names = this.devs.map(nameOf).join(' + ');
    this.onStatus(() => t.devices.pressToTest(names), 'ok');
  }

  /** Decodes one packet of controller `src` (its device id; `tag` is its name, for the raw log and the key prefix). */
  parse(b: Uint8Array, tag: string, src: string = tag) {
    this.onRaw?.(tag, b);
    if (b.length < 2) return;
    const op = b[0];
    const f = protoVarints(b, 1);
    const now = new Set<string>();
    if (op === 0x23 && f[1] != null) {
      // Zwift Ride: a mask where each bit at 0 signals a pressed button
      // a bit only counts as a button after having been seen released (at 1) at least once
      const m = f[1];
      const seen = this.seen.get(src) ?? new Set<number>();
      this.seen.set(src, seen);
      for (let i = 0; i < 32; i++) { const on = Math.floor(m / 2 ** i) & 1; if (on) seen.add(i); else if (seen.has(i)) now.add('bit' + i); }
    } else if (op === 0x07 || op === 0x37) {
      // Zwift Play / Click: one field per button, 0 = pressed
      for (const k in f) if (f[k] === 0) now.add(`${tag.includes('Click') ? 'click' : 'play'}${op}_f${k}`);
    } else return;
    this.setHeld(src, now);
  }

  disconnect() {
    for (const d of this.devs) d.gatt?.disconnect();
    for (const src of [...this.held.keys()]) this.setHeld(src, new Set());
    this.devs = [];
  }
}

export function buttonLabel(key: string) {
  const m = /^bit(\d+)$/.exec(key);
  if (m) return t.devices.buttons[+m[1]] || t.devices.button(m[1]);
  return key;
}
